import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/common/Reveal";
import { CoffeeSection } from "../components/home/CoffeeSection";
import { articleImgFallback, onImgError } from "../lib/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  {
    id: "coffee-dental",
    name: "Coffee With Dr. Irma · Dental",
    topics: "Dental Health · Smiles · Modern Dentistry",
    accent: "text-periwinkle",
    bar: "bg-periwinkle",
    path: "/dental/ms/kolum",
  },
  {
    id: "coffee-growth",
    name: "Coffee With Dr. Irma · Growth",
    topics: "Entrepreneurship · Business · Leadership · Human Capital · Life",
    accent: "text-burgundy",
    bar: "bg-burgundy",
    path: "/consulting/insights",
  },
  {
    id: "coffee-stories",
    name: "Coffee With Dr. Irma · Stories",
    topics: "Books · Culture · Ideas · Reflections · Creativity",
    accent: "text-amber",
    bar: "bg-amber",
    path: "/creative/stories",
  },
];

const articlePath = (a) =>
  a.channel === "coffee-dental"
    ? `/dental/${a.language || "ms"}/kolum/${a.slug}`
    : a.channel === "coffee-stories"
      ? `/creative/stories/${a.slug}`
      : `/consulting/insights/${a.slug}`;

const HUB_FILTERS = [
  { id: "all", label: "All" },
  { id: "dental", label: "Dental" },
  { id: "consulting", label: "Business & Growth" },
  { id: "publishing", label: "Life & Writing" },
];

export default function CoffeePage() {
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetch(`${API}/content?series=coffee-with-dr-irma`).then((r) => r.json()).then((data) => setArticles(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const shown = filter === "all" ? articles : articles.filter((a) => a.vertical === filter);

  return (
    <main data-testid="coffee-page">
      <section className="bg-warmwhite py-24 lg:py-32" data-testid="coffee-hero">
        <div className="container-site">
          <motion.p
            className="eyebrow"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            The editorial voice of iINSPIRE
          </motion.p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            Coffee with <span className="italic text-burgundy">Dr. Irma</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/75 sm:text-lg">
            Essays, articles and conversations spanning all three worlds —
            written by Dr. Irma, DDS (UKM).
          </p>
        </div>
      </section>

      <section className="bg-softgrey py-20 lg:py-28" data-testid="coffee-channels">
        <div className="container-site grid gap-6 lg:grid-cols-3">
          {CHANNELS.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.1} className="h-full">
              <Link to={c.path} className="group flex h-full flex-col border border-ink/10 bg-white p-8 transition-shadow duration-300 hover:shadow-xl" data-testid={`channel-${c.id}`}>
                <span className={`block h-0.5 w-10 ${c.bar}`} aria-hidden="true" />
                <h2 className="mt-6 font-serif text-xl leading-snug text-ink">{c.name}</h2>
                <p className={`mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] ${c.accent}`}>
                  {c.topics}
                </p>
                <span className="link-arrow mt-6">
                  Explore <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-warmwhite py-20 lg:py-28" data-testid="coffee-articles">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow">Latest writing</p>
          </Reveal>
          {articles.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2" data-testid="coffee-hub-filters">
              {HUB_FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  data-testid={`coffee-filter-${f.id}`}
                  className={`px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] transition-colors duration-200 ${filter === f.id ? "bg-ink text-warmwhite" : "border border-ink/15 text-ink/60 hover:border-amber hover:text-ink"}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
          {articles.length === 0 ? (
            <Reveal delay={0.1}>
              <p className="mt-8 border-l-2 border-amber pl-6 font-serif text-xl italic text-ink/70" data-testid="articles-empty">
                The first essays are being written. Published pieces will appear here.
              </p>
            </Reveal>
          ) : (
            <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {shown.map((a) => (
                <Link key={a.id} to={articlePath(a)} className="group flex h-full flex-col overflow-hidden border border-ink/10 bg-white transition-shadow duration-300 hover:shadow-xl" data-testid={`article-${a.slug}`}>
                  <div className="h-44 shrink-0 overflow-hidden">
                    <img src={a.featured_image || articleImgFallback(a.vertical)} alt={a.alt_text || a.title} loading="lazy" onError={onImgError(a.vertical)} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber">{a.category}</p>
                    <h3 className="mt-3 font-serif text-lg leading-snug text-ink">{a.title}</h3>
                    {a.excerpt && <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/70 line-clamp-3">{a.excerpt}</p>}
                    <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-ink/45">Written by {a.author}</p>
                    <span className="link-arrow mt-5">
                      Read <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <CoffeeSection />
    </main>
  );
}
